import React, { useMemo } from 'react'
import { cn } from '../../lib/utils'
import { yearBudget, formatCost } from '../../lib/capex-utils'
import type { Worksite } from './types'
import { useCapexCurrency } from './CapexCurrencyContext'

interface BudgetFooterProps {
  worksites: Worksite[]
  horizonStart: number
  horizonEnd: number
}

export const BudgetFooter = React.memo(function BudgetFooter({
  worksites,
  horizonStart,
  horizonEnd,
}: BudgetFooterProps) {
  const capexCurrency = useCapexCurrency()
  const years: number[] = []
  for (let y = horizonStart; y <= horizonEnd; y++) {
    years.push(y)
  }

  const budgets = useMemo(
    () => years.map((year) => yearBudget(worksites, year)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [worksites, horizonStart, horizonEnd]
  )

  const maxBudget = useMemo(() => Math.max(...budgets, 1), [budgets])

  function barColor(budget: number): string {
    const ratio = budget / maxBudget
    if (ratio > 0.8) return 'bg-red-500'
    if (ratio > 0.5) return 'bg-amber-500'
    return 'bg-blue-500'
  }

  return (
    <tfoot data-testid="budget-footer">
      <tr>
        {/* Label cell */}
        <td className="sticky left-0 z-10 bg-background border-t border-r border-border px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Total
        </td>
        {years.map((year, i) => {
          const budget = budgets[i]
          const barHeightPct = maxBudget > 0 ? (budget / maxBudget) * 100 : 0
          return (
            <td
              key={year}
              className="border-t border-border px-2 py-2 text-center align-bottom"
              aria-label={`Budget ${year}: ${formatCost(budget, capexCurrency)}`}
              data-testid={`budget-bar-${year}`}
            >
              <div className="flex flex-col items-center gap-1">
                {budget > 0 && (
                  <span className="text-xs font-medium text-foreground">
                    {formatCost(budget, capexCurrency)}
                  </span>
                )}
                <div className="w-full h-8 flex items-end justify-center">
                  {budget > 0 && (
                    <div
                      className={cn('w-6 rounded-t-sm transition-all', barColor(budget))}
                      style={{ height: `${Math.max(barHeightPct, 8)}%` }}
                    />
                  )}
                </div>
              </div>
            </td>
          )
        })}
      </tr>
    </tfoot>
  )
})
